import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Check, Info, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { cn } from '../../lib/cn.js';
import { FOCUS_RING, FOCUS_RING_ON_DARK } from '../../lib/focus-ring.js';
/**
 * Toast (handoff, Molecules): one short confirmation of something that
 * happened, for an action that moved the user somewhere else — creating a
 * practice and landing on it, "Save & close" and landing on the home screen.
 * Shown through `useToast`, not mounted by hand; see `Toaster.tsx`.
 *
 * When the user stays put, the confirmation belongs next to the control they
 * touched, which is `InlineConfirmation` below. A toast for an in-place change
 * is feedback in the wrong corner of the screen.
 *
 * `role="status"` rather than `alert`: this is a confirmation, not an
 * interruption.
 */
export function Toast({ children, tone = 'success', onDismiss, dismissLabel, live = true, className, }) {
    return (_jsxs("div", { ...(live ? { role: 'status' } : {}), className: cn('flex items-center gap-4 rounded-md bg-text py-3 pl-6 text-body font-medium text-text-inverse', 
        // Room on the right for the close button's own padding, or the same
        // padding as the left when there is none.
        onDismiss ? 'pr-2' : 'pr-6', 
        // Near-black, where the primary ring would be 2.4:1: anything
        // focusable a toast is given takes the accent instead.
        FOCUS_RING_ON_DARK, className), children: [_jsx("span", { "aria-hidden": "true", className: cn('flex size-7 shrink-0 items-center justify-center rounded-full', tone === 'success' ? 'bg-accent' : 'bg-text-secondary'), children: tone === 'success' ? (_jsx(Check, { className: "size-icon-sm text-avatar-on-accent", strokeWidth: 2.5 })) : (_jsx(Info, { className: "size-icon-sm text-text-inverse", strokeWidth: 2.5 })) }), _jsx("span", { className: "min-w-0 flex-1 py-2", children: children }), onDismiss ? (_jsx("button", { type: "button", onClick: onDismiss, "aria-label": dismissLabel, className: cn('flex size-touch-target-min shrink-0 items-center justify-center rounded-sm', 'text-text-disabled transition-colors duration-fast hover:text-text-inverse', FOCUS_RING), children: _jsx(X, { "aria-hidden": "true", className: "size-icon-md" }) })) : null] }));
}
/**
 * Inline confirmation (handoff, Molecules, and UX principle 5: every state
 * change is confirmed in place, not inferred). Sits next to the control that
 * changed, for a change the user stays on the page for. "Settings saved."
 *
 * ── Why the words arrive a moment after the region ─────────────────────
 *
 * Callers render this only once there is something to confirm, so it would
 * arrive already holding its sentence — and a status region that appears
 * with its text in it is frequently not read at all, which `LiveRegion` below
 * exists to avoid. So the region renders empty and the sentence goes in on
 * the next pass: what a screen reader sees is a status whose text changed,
 * which it announces. Nothing is drawn until then, so the one frame shows no
 * empty box. A later confirmation in the same place (a different student
 * assigned) changes the text of the same region, and is announced the same
 * way.
 */
export function InlineConfirmation({ children, className }) {
    const [ready, setReady] = useState(false);
    useEffect(() => setReady(true), []);
    return (_jsx("p", { role: "status", className: className, children: ready ? (_jsxs("span", { className: cn('inline-flex items-center gap-2 rounded-md border border-border bg-surface-secondary', 'px-4 py-2 text-body-sm text-text-secondary'), children: [_jsx(Info, { "aria-hidden": "true", className: "size-icon-sm shrink-0 text-text-muted" }), _jsx("span", { children: children })] })) : null }));
}
/**
 * The announcer behind every piece of feedback (handoff, Accessibility:
 * "Live regions announce parse results, save confirmations, and submission
 * results").
 *
 * Rendered always and updated in place. A live region mounted at the same
 * moment as its message is frequently missed by screen readers, because there
 * was no region there to observe changing.
 */
export function LiveRegion({ message, politeness = 'polite' }) {
    return (_jsx("div", { "aria-live": politeness, "aria-atomic": "true", className: "sr-only", children: message }));
}
//# sourceMappingURL=Feedback.js.map